import { useState, useEffect } from "react";
import {
    Button,
    Form,
    Input,
    Layout,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    message,
    Typography,
    Alert,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import isEmail from "validator/lib/isEmail";
import isIP from "validator/lib/isIP";
import { format } from "date-fns";
import { fromUTC } from "common/utils";
import {
    searchBlacklist,
    addBlacklistEntry,
    deactivateBlacklistEntry,
    getBrandsForEmail,
} from "./blacklist.api";

const { Content } = Layout;
const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 50;

const typeOptions = [
    { value: "Email", label: "Email" },
    { value: "Ip", label: "IP address" },
];

export const Blacklist = () => {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [entryType, setEntryType] = useState(null);
    const [search, setSearch] = useState("");
    const [activeOnly, setActiveOnly] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // Enable the Add button only while the form passes validation.
    const formValues = Form.useWatch([], form);
    const [submittable, setSubmittable] = useState(false);

    useEffect(() => {
        form.validateFields({ validateOnly: true })
            .then(() => setSubmittable(true))
            .catch(() => setSubmittable(false));
    }, [form, formValues]);

    // Reads current filter/pagination state, with optional per-call overrides.
    const fetchEntries = async (opts = {}) => {
        const pick = (key, fallback) =>
            opts[key] !== undefined ? opts[key] : fallback;

        const _entryType = pick("entryType", entryType);
        const _search = pick("search", search);
        const _activeOnly = pick("activeOnly", activeOnly);
        const _page = pick("page", page);
        const _pageSize = pick("pageSize", pageSize);

        setLoading(true);
        try {
            const result = await searchBlacklist({
                entryType: _entryType || undefined,
                search: _search?.trim() || undefined,
                isActive: _activeOnly ? true : undefined,
                pageIndex: _page - 1,
                pageSize: _pageSize,
            });
            setEntries(result);
            setTotal(result.length ? result[0].totalCount : 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => {
        setPage(1);
        fetchEntries({ page: 1 });
    };

    const handleTableChange = (pagination) => {
        setPage(pagination.current);
        setPageSize(pagination.pageSize);
        fetchEntries({
            page: pagination.current,
            pageSize: pagination.pageSize,
        });
    };

    const handleDeactivate = async (id) => {
        setLoading(true);
        try {
            const res = await deactivateBlacklistEntry(id);
            if (res?.success) {
                message.success("Entry deactivated.");
                await fetchEntries();
            } else {
                message.error(res?.message || "Failed to deactivate entry.");
            }
        } finally {
            setLoading(false);
        }
    };

    const submitEntry = async (values) => {
        setSaving(true);
        try {
            const res = await addBlacklistEntry({
                entryType: values.entryType,
                value: values.value?.trim(),
                reason: values.reason?.trim() || null,
            });
            if (res?.success) {
                message.success("Blacklist entry added.");
                setModalOpen(false);
                form.resetFields();
                applyFilters();
            } else {
                message.error(res?.message || "Failed to add entry.");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        const value = values.value?.trim();

        // Blacklisting an email removes the ambassador from every program they're
        // currently in, across every brand they belong to — warn the admin which
        // brands that will affect before it happens.
        if (values.entryType === "Email" && value) {
            setSaving(true);
            let brands;
            try {
                brands = await getBrandsForEmail(value);
            } finally {
                setSaving(false);
            }

            if (brands?.length > 0) {
                Modal.confirm({
                    centered: true,
                    icon: null,
                    className: "blacklist-confirm-modal",
                    content: (
                        <div style={{ width: "100%" }}>
                            <Alert
                                type="warning"
                                message={
                                    <div>
                                        Blacklisting this email will remove them
                                        from{" "}
                                        <Text strong underline>
                                            all
                                        </Text>{" "}
                                        programs
                                    </div>
                                }></Alert>
                            <p style={{ margin: "12px 0 8px" }}>
                                <Text strong>{value}</Text> is currently an
                                ambassador in {brands.length} brand
                                {brands.length > 1 ? "s" : ""}:
                            </p>
                            <Space size={[8, 8]} wrap>
                                {brands.map((brand) => (
                                    <Tag
                                        key={brand}
                                        color="gold"
                                        style={{ margin: 0 }}>
                                        {brand}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    ),
                    okText: "Blacklist and remove",
                    okButtonProps: { danger: true },
                    cancelText: "Cancel",
                    onOk: () => submitEntry(values),
                });
                return;
            }
        }

        await submitEntry(values);
    };

    // Validates the value field against the currently selected entry type.
    const validateValue = (_, value) => {
        const v = (value || "").trim();
        if (!v) {
            return Promise.resolve(); // empty is handled by the required rule
        }
        if (form.getFieldValue("entryType") === "Ip") {
            return isIP(v)
                ? Promise.resolve()
                : Promise.reject(
                      new Error("Enter a valid IPv4 or IPv6 address."),
                  );
        }
        return isEmail(v)
            ? Promise.resolve()
            : Promise.reject(new Error("Enter a valid email address."));
    };

    const columns = [
        { title: "Value", dataIndex: "value", key: "value" },
        {
            title: "Type",
            dataIndex: "entryType",
            key: "entryType",
            width: 90,
            render: (t) => <Tag>{t}</Tag>,
        },
        { title: "Reason", dataIndex: "reason", key: "reason" },
        {
            title: "Source",
            dataIndex: "source",
            key: "source",
            width: 130,
            render: (s) => (
                <Tag color={s === "AutoBlocked" ? "orange" : "blue"}>{s}</Tag>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            width: 100,
            render: (active) =>
                active ? <Tag color="blue">Active</Tag> : <Tag>Inactive</Tag>,
        },
        {
            title: "Added By",
            dataIndex: "createdBy",
            key: "createdBy",
            width: 220,
        },
        {
            title: "Added",
            dataIndex: "insertDate",
            key: "insertDate",
            width: 170,
            render: (d) => {
                const date = d ? fromUTC(d) : null;
                return date && !isNaN(date.getTime())
                    ? format(date, "dd MMM yyyy '@' h:mma")
                    : "";
            },
        },
        {
            title: "",
            key: "actions",
            width: 120,
            render: (_, record) =>
                record.isActive ? (
                    <Popconfirm
                        title="Deactivate this entry?"
                        onConfirm={() => handleDeactivate(record.id)}
                        okText="Deactivate"
                        cancelText="Cancel">
                        <Button danger size="small">
                            Deactivate
                        </Button>
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <Content style={{ padding: 24 }}>
            <div
                style={{
                    marginBottom: 16,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                }}>
                <Select
                    allowClear
                    placeholder="All types"
                    value={entryType}
                    onChange={(v) => setEntryType(v || null)}
                    style={{ width: 140 }}
                    options={typeOptions}
                />
                <Input
                    allowClear
                    placeholder="Search value"
                    value={search}
                    onChange={(e) => setSearch(e?.target?.value || "")}
                    onPressEnter={applyFilters}
                    style={{ width: 280 }}
                />
                <Select
                    value={activeOnly}
                    onChange={(v) => setActiveOnly(v)}
                    style={{ width: 160 }}
                    options={[
                        { value: true, label: "Active only" },
                        { value: false, label: "All statuses" },
                    ]}
                />
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={applyFilters}
                    loading={loading}>
                    Search
                </Button>
                <Button
                    icon={<PlusOutlined />}
                    onClick={() => setModalOpen(true)}
                    style={{ marginLeft: "auto" }}>
                    Add Entry
                </Button>
            </div>

            <Table
                bordered
                columns={columns}
                dataSource={entries}
                loading={loading}
                rowKey="id"
                size="small"
                onChange={handleTableChange}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: total,
                    position: ["bottomCenter"],
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100],
                    showTotal: (t) => `Total ${t} items`,
                }}
                scroll={{ y: "calc(100vh - 220px)", x: 1000 }}
                style={{ width: "100%" }}
            />

            <Modal
                title="Add Blacklist Entry"
                open={modalOpen}
                onOk={handleAdd}
                confirmLoading={saving}
                okText="Add"
                okButtonProps={{ disabled: !submittable }}
                onCancel={() => {
                    setModalOpen(false);
                    form.resetFields();
                }}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ entryType: "Email" }}>
                    <Form.Item
                        name="entryType"
                        label="Type"
                        rules={[{ required: true, message: "Select a type." }]}>
                        <Select options={typeOptions} />
                    </Form.Item>
                    <Form.Item
                        name="value"
                        label="Value"
                        dependencies={["entryType"]}
                        rules={[
                            { required: true, message: "Enter a value." },
                            { validator: validateValue },
                        ]}>
                        <Input placeholder="email@example.com or 203.0.113.5" />
                    </Form.Item>
                    <Form.Item name="reason" label="Reason">
                        <Input.TextArea rows={3} placeholder="Optional note" />
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};
